import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PesoBadge from '../components/PesoBadge';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Briefcase, MapPin, Star, Heart, DollarSign, Plus, Sparkles, ArrowRight, Loader2, Shield, Users, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ContactUsButton from '../components/ContactUsButton';
import ReviewModal from '../components/ReviewModal';
import AISuggestionsModal from '../components/AISuggestionsModal';
import PastEmploymentSection from '../components/PastEmploymentSection';
import ReferralSection from '../components/ReferralSection';
import MyServiceListingsSection from '../components/MyServiceListingsSection';
import MyServiceRequestsSection from '../components/MyServiceRequestsSection';
import MatchingRequestsSection from '../components/MatchingRequestsSection';
import ProCredentialsSection from '../components/ProCredentialsSection';
import PendingReviewsCard from '../components/PendingReviewsCard';
import MyApplicationsTable from '../components/MyApplicationsTable';
import OtherJobsForYou from '../components/OtherJobsForYou';
import SavedJobsTable from '../components/SavedJobsTable';
import UpcomingInterviews from '../components/UpcomingInterviews';
import { useLiveJobs } from '../lib/realtime-hooks';
import { useSavedJobs } from '../hooks/useSavedJobs';
import { useOnboarding } from '../hooks/useOnboarding';
import { OnboardingTour, OnboardingChecklist } from '../components/onboarding';
import ContinueApplicationCard from '../components/ContinueApplicationCard';
import JobAlertsSetting from '../components/JobAlertsSetting';
import api from '../lib/api';

// Recommendations Banner Component
const RecommendationsBanner = ({ profile, navigate, t, onSeeAll }) => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({
    profile_improvements: [],
    job_matches: [],
    gig_matches: []
  });

  useEffect(() => {
    fetchRecommendations();
  }, [profile]);

  const fetchRecommendations = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        console.log('No session token');
        setLoading(false);
        return;
      }

      const response = await api.get('/api/ai/suggestions', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      console.log('Recommendations API Response:', response.data);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-primary" size={20} />
            <h2 className="font-heading text-lg font-semibold">{t('dashboard.recommendedForYou')}</h2>
          </div>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasAnyRecommendations =
    suggestions.job_matches.length > 0 ||
    suggestions.gig_matches.length > 0;

  if (!hasAnyRecommendations) {
    return null; // Hide if truly no recommendations
  }

  const jobAndGigMatches = [...suggestions.job_matches, ...suggestions.gig_matches].slice(0, 3);
  const getJobUrl = (job) => job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`;

  return (
    <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20 card-enhanced">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-primary" size={20} />
            <h2 className="font-heading text-lg font-semibold">{t('dashboard.recommendedForYou')}</h2>
          </div>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            AI-Powered
          </Badge>
        </div>

        {/* Job & Gig Matches Section */}
        {jobAndGigMatches.length > 0 && (
          <>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Briefcase size={16} className="text-primary" />
              {t('dashboard.jobMatches')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {jobAndGigMatches.map((job, index) => (
                <Card
                  key={job.id || index}
                  className={`hover:shadow-lg transition-all cursor-pointer ${job.is_peso_match ? 'border-2 border-[#1d4ed8] bg-blue-50/40' : 'border-primary/10 hover:border-primary/30'}`}
                  onClick={() => navigate(getJobUrl(job))}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-sm line-clamp-2">{job.title}</h3>
                      <div className="flex gap-1 ml-2 flex-shrink-0 flex-wrap justify-end">
                        {job.is_peso_match && (
                          <PesoBadge officeName={job.peso_office_name} color={job.peso_office_color} title={t('peso.recPriorityNote')} />
                        )}
                        {job.is_new && (
                          <Badge className="bg-green-500 text-white text-[10px] px-1.5">
                            New
                          </Badge>
                        )}
                        {job.positions_available > 1 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-medium dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
                            <Users size={10} /> Hiring {job.positions_available}
                          </span>
                        )}
                        {job.post_type === 'gig' && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            Gig
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 text-xs text-muted-foreground">
                      {job.employer_name && (
                        <div className="flex items-center gap-1">
                          <Briefcase size={12} />
                          <span className="truncate">{job.employer_name}</span>
                        </div>
                      )}
                      {job.location && (
                        <div className="flex items-center gap-1">
                          <MapPin size={12} />
                          <span>{job.location}</span>
                        </div>
                      )}
                      {(job.salary_min || job.salary_max) && (
                        <div className="flex items-center gap-1 text-muted-foreground text-xs">
                          <span>
                            ₱{job.salary_min?.toLocaleString()} - ₱{job.salary_max?.toLocaleString()}
                            {job.salary_period && ` per ${job.salary_period === 'daily' ? 'day' : 'month'}`}
                          </span>
                        </div>
                      )}
                      {(job.match_reason || job.match_reason_ceb) && (
                        <p className="text-xs text-muted-foreground italic mt-1">
                          {i18n.language === 'ceb' && job.match_reason_ceb ? job.match_reason_ceb : job.match_reason}
                        </p>
                      )}
                    </div>

                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-3 text-primary hover:bg-primary/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(getJobUrl(job));
                      }}
                    >
                      {t('dashboard.viewDetails')}
                      <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        <div className="mt-4 text-center">
          <Button 
            variant="outline" 
            size="sm"
            className="text-primary border-primary/20 hover:bg-primary/10 hover:text-primary"
            onClick={onSeeAll}
          >
            <Sparkles size={16} className="mr-2" />
            {t('dashboard.seeAllRecommendations')}
            <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Profile Completeness Bar Component
const COMPLETENESS_DISMISS_KEY = 'bohol_completeness_dismissed';

const computeCompleteness = (profile) => {
  if (!profile) return { percentage: 0, nextAction: null };

  let score = 0;
  const fields = [];

  // skills (25%)
  const hasSkills = profile.skills && profile.skills.length >= 3;
  if (hasSkills) score += 25;
  else fields.push({ key: 'skills', weight: 25, label: 'addSkills.title', link: '/profile?edit=true' });

  // work_history or experience (20%)
  const hasWorkHistory = (profile.work_history && profile.work_history.length >= 1) ||
    (profile.experience && profile.experience.length >= 100);
  if (hasWorkHistory) score += 20;
  else fields.push({ key: 'work_history', weight: 20, label: 'completeness.addWorkHistory', link: '/profile?edit=true' });

  // salary_min (15%)
  const hasSalary = profile.salary_min != null && profile.salary_min > 0;
  if (hasSalary) score += 15;
  else fields.push({ key: 'salary', weight: 15, label: 'completeness.setSalary', link: '/profile?edit=true' });

  // availability (15%)
  const hasAvailability = profile.availability && profile.availability.length >= 1;
  if (hasAvailability) score += 15;
  else fields.push({ key: 'availability', weight: 15, label: 'completeness.setAvailability', link: '/profile?edit=true' });

  // preferred_locations (10%)
  const hasLocations = profile.preferred_locations && profile.preferred_locations.length >= 1;
  if (hasLocations) score += 10;
  else fields.push({ key: 'locations', weight: 10, label: 'completeness.addLocations', link: '/profile?edit=true' });

  // avatar_url (10%)
  const hasAvatar = profile.avatar_url && profile.avatar_url.trim() !== '';
  if (hasAvatar) score += 10;
  else fields.push({ key: 'avatar', weight: 10, label: 'completeness.addPhoto', link: '/profile?edit=true' });

  // tagline (5%)
  const hasTagline = profile.tagline && profile.tagline.trim() !== '';
  if (hasTagline) score += 5;
  else fields.push({ key: 'tagline', weight: 5, label: 'completeness.addTagline', link: '/profile?edit=true' });

  // Next action = highest weight missing field (already sorted by weight since we push in order)
  const nextAction = fields.length > 0 ? fields[0] : null;

  return { percentage: score, nextAction };
};

const RESUME_BANNER_DISMISS_KEY = 'bohol_resume_banner_dismissed';

/**
 * Encourages users without a resume on file to upload one. Dismissable.
 * Only renders if profile has no resume_file_url AND user hasn't dismissed it.
 */
const ResumeUploadBanner = ({ profile, navigate, t }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(RESUME_BANNER_DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed || !profile || profile.resume_file_url) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(RESUME_BANNER_DISMISS_KEY, 'true'); } catch {}
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 relative">
      <button
        onClick={handleDismiss}
        aria-label={t('common.dismiss', 'Dismiss')}
        className="absolute top-2 right-2 text-amber-700 hover:text-amber-900 text-lg leading-none px-2 py-1"
      >
        ×
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Sparkles className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-amber-900">
            {t('resume.banner.title', 'Upload your resume to fill your profile in seconds')}
          </h3>
          <p className="text-sm text-amber-800 mt-1">
            {t('resume.banner.description', "Skip the typing — we'll read your PDF or photo and auto-fill your skills, experience, and more.")}
          </p>
          <Button
            size="sm"
            className="mt-3 bg-amber-600 hover:bg-amber-700"
            onClick={() => navigate('/profile')}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {t('resume.banner.action', 'Upload resume')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ProfileCompletenessBar = ({ profile, navigate, t }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(COMPLETENESS_DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const { percentage, nextAction } = useMemo(() => computeCompleteness(profile), [profile]);

  if (dismissed || percentage >= 90 || !nextAction) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(COMPLETENESS_DISMISS_KEY, 'true');
    } catch {}
  };

  return (
    <Card className="border-primary/20 overflow-hidden">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={18} className="text-primary" />
            <span className="font-semibold text-sm">
              {t('completeness.title', 'Profile {{percent}}% complete', { percent: percentage })}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-2.5 mb-3">
          <div
            className="bg-primary h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('completeness.nextStep', 'Complete your profile to get better job matches')}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="ml-3 flex-shrink-0 text-primary border-primary/30 hover:bg-primary/10"
            onClick={() => navigate(nextAction.link)}
          >
            {t(nextAction.label)}
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardSeeker = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewContext, setReviewContext] = useState(null);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const { savedJobIds, toggleSaveJob } = useSavedJobs(profile);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const { jobs: recommendedJobs } = useLiveJobs({});

  // Onboarding stats for auto-completion detection
  const onboardingStats = useMemo(() => ({
    applicationsCount: myApplications.length,
    profileCompleteness: profile?.profile_completeness || 0
  }), [myApplications, profile]);

  // Onboarding hook
  const {
    tourSteps,
    checklistItems,
    completedItems,
    progress,
    allCompleted,
    showChecklist,
    shouldRunTour,
    tourStepIndex,
    startTour,
    handleChecklistDismiss,
    handleJoyrideCallback,
    tourJustFinished
  } = useOnboarding(profile, onboardingStats);

  useEffect(() => {
    fetchMyApplications();
    fetchSavedJobs();

    if (profile?.id) {
      const channel = supabase
        .channel('seeker-applications-and-bids')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'applications',
            filter: `seeker_id=eq.${profile.id}`
          },
          (payload) => {
            console.log('🔄 Application change detected:', payload);
            fetchMyApplications();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bids',
            filter: `contractor_id=eq.${profile.id}`
          },
          (payload) => {
            console.log('🔄 Bid change detected:', payload);
            fetchMyApplications();
          }
        )
        // Also listen for notifications to catch application_viewed events
        // This is a backup in case applications table realtime isn't enabled
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${profile.id}`
          },
          (payload) => {
            const notifType = payload.new?.type;
            // Refresh applications when employer views our application or bid
            if (notifType === 'application_viewed' || notifType === 'bid_viewed' || notifType === 'application_update') {
              console.log('🔔 Received view notification, refreshing applications:', notifType);
              fetchMyApplications();
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [profile]);

  const fetchMyApplications = async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use backend API instead of direct Supabase query to bypass RLS
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${session.session.access_token}` };
      
      // Fetch both applications (for jobs) and bids (for gigs) in parallel
      const [applicationsResponse, bidsResponse] = await Promise.all([
        api.get('/api/applications/my', { headers }),
        api.get('/api/bids/my', { headers }).catch(err => {
          console.error('❌ Error fetching bids:', err.response?.data || err.message);
          return { data: { bids: [] } };
        })
      ]);
      
      console.log('✅ Applications from backend:', applicationsResponse.data);
      console.log('✅ Bids from backend:', bidsResponse.data);
      console.log('📊 Combined items count:', {
        applications: applicationsResponse.data.applications?.length || 0,
        bids: bidsResponse.data.bids?.length || 0
      });
      
      // Combine applications and bids into one array
      const applications = (applicationsResponse.data.applications || []).map(app => ({
        ...app,
        type: 'application' // Mark as application for UI
      }));
      
      const bids = (bidsResponse.data.bids || []).map(bid => ({
        ...bid,
        type: 'bid', // Mark as bid for UI
        // Map bid fields to match application structure for unified display
        job: bid.gig ? {
          ...bid.gig,
          // Map gig-specific fields to match job structure
          employer: bid.gig.owner, // Gigs use owner, jobs use employer
          employer_id: bid.gig.owner_id,
          salary_min: bid.gig.budget_min, // Gigs use budget, jobs use salary
          salary_max: bid.gig.budget_max
        } : null,
        status: bid.status || 'pending'
      }));
      
      // Combine and sort by created_at (newest first)
      const combined = [...applications, ...bids].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setMyApplications(combined);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    setLoadingSaved(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) return;

      const response = await api.get('/api/jobs/saved', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      setSavedJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch saved jobs:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleUnsaveJob = async (jobId) => {
    await toggleSaveJob(jobId);
    fetchSavedJobs();
  };

  const handleOpenReview = (targetUser, context) => {
    setReviewTarget(targetUser);
    setReviewContext(context);
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-7xl mx-auto space-y-8">
        {/* Welcome Section */}
        <div data-tour="seeker-welcome" className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                {t('dashboard.welcomeBack', { name: profile.name?.split(' ')[0] })} 👋
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">{t('dashboard.readyForOpportunity')}</p>
            </div>
          </div>
        </div>

        {/* Continue your application (only if a remembered job is still pending) */}
        <ContinueApplicationCard navigate={navigate} t={t} />

        {/* Profile Completeness Bar */}
        <ProfileCompletenessBar profile={profile} navigate={navigate} t={t} />

        {/* Job alerts on/off (new-job push + in-app; default ON) */}
        <JobAlertsSetting profile={profile} />

        {/* ID Verification Banner - shown if not verified */}
        {!profile?.verified && (
          <div data-tour="seeker-verify-id" className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">{t('onboarding.verifyIdBanner.title')}</h3>
                <p className="text-sm text-blue-700 mt-1">{t('onboarding.verifyIdBanner.description')}</p>
                <Button
                  size="sm"
                  className="mt-3 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/profile')}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {t('onboarding.verifyIdBanner.action')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Resume Upload Banner - shown if no resume yet and not dismissed */}
        <ResumeUploadBanner profile={profile} navigate={navigate} t={t} />

        {/* Upcoming Interviews */}
        <UpcomingInterviews isEmployer={false} limit={5} />

        {/* Recommendations Banner */}
        <div data-tour="seeker-recommendations">
          <RecommendationsBanner profile={profile} navigate={navigate} t={t} onSeeAll={() => setShowAISuggestions(true)} />
        </div>

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="applications" data-tour="seeker-applications" className="text-xs sm:text-sm">{t('dashboard.myApplications')} ({myApplications.length})</TabsTrigger>
            <TabsTrigger value="saved" className="text-xs sm:text-sm">{t('dashboard.savedJobs')} ({savedJobs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            {myApplications.length === 0 ? (
              <Card className="p-6 sm:p-12 text-center">
                <Briefcase className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noApplicationsYet')}</h3>
                <p className="text-muted-foreground mb-4">{t('dashboard.noApplicationsDesc')}</p>
                <Button data-tour="seeker-browse-jobs" onClick={() => navigate('/jobs')} size="lg" className="rounded-xl">{t('dashboard.browseJobsNow')}</Button>
              </Card>
            ) : (
              <>
                <OtherJobsForYou applications={myApplications} t={t} />
                <MyApplicationsTable applications={myApplications} />
              </>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {loadingSaved ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : savedJobs.length === 0 ? (
              <Card className="p-6 sm:p-12 text-center">
                <Heart className="mx-auto mb-4 text-muted-foreground" size={48} />
                <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noSavedJobsYet')}</h3>
                <p className="text-muted-foreground mb-4">{t('dashboard.noSavedJobsDesc')}</p>
                <Button onClick={() => navigate('/jobs')} className="rounded-xl">{t('dashboard.browseJobs')}</Button>
              </Card>
            ) : (
              <SavedJobsTable jobs={savedJobs} onUnsave={handleUnsaveJob} />
            )}
          </TabsContent>
        </Tabs>

        {/* Past Employment Section */}
        <PastEmploymentSection profile={profile} />

        {/* My Services (Services Marketplace listings) — self-hides when the flag is off */}
        <div className="mt-8">
          <MyServiceListingsSection profile={profile} />
        </div>

        {/* My service requests (client "I need X" posts) — self-hides when the flag is off */}
        <div className="mt-8">
          <MyServiceRequestsSection profile={profile} />
        </div>

        {/* Matching requests for Pros — self-hides when flag off or no active listing */}
        <div className="mt-8">
          <MatchingRequestsSection profile={profile} />
        </div>

        {/* Services action queue: hire confirmations + pending reviews — self-hides when flag off/empty */}
        <div className="mt-8">
          <PendingReviewsCard profile={profile} />
        </div>

        {/* My Credentials — self-hides when the flag is off */}
        <div className="mt-8">
          <ProCredentialsSection profile={profile} />
        </div>
      </div>

      {/* Ambassador Referral Section — only for ambassadors */}
      {profile?.is_referrer && (
        <div className="container mx-auto px-4 mt-8">
          <ReferralSection profile={profile} />
        </div>
      )}

      {/* Need Help Section */}
      <div className="container mx-auto px-4 pb-nav md:pb-8 mt-8">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 text-center border border-blue-200">
          <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Have questions or need support? Our team is here to help!
          </p>
          <ContactUsButton variant="default" className="bg-blue-600 hover:bg-blue-700" />
        </div>
      </div>

      <ReviewModal
        open={!!reviewTarget}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        targetUser={reviewTarget}
        context={reviewContext?.context}
        contextId={reviewContext?.context_id}
        onSuccess={fetchMyApplications}
      />
      
      <AISuggestionsModal
        open={showAISuggestions}
        onOpenChange={setShowAISuggestions}
        profile={profile}
      />
      
      <BottomNav profile={profile} />

      {/* Onboarding Tour */}
      <OnboardingTour
        steps={tourSteps}
        run={shouldRunTour}
        stepIndex={tourStepIndex}
        onCallback={handleJoyrideCallback}
      />

      {/* Onboarding Checklist */}
      {showChecklist && (
        <OnboardingChecklist
          items={checklistItems}
          completedItems={completedItems}
          progress={progress}
          onDismiss={handleChecklistDismiss}
          onRestartTour={startTour}
          allCompleted={allCompleted}
          defaultMinimized={true}
        />
      )}
    </div>
  );
};

export default DashboardSeeker;